import React, { useCallback, useState } from "react";

import Alert from "../Alert";
import { LabelVariant } from "../InputLabel";
import TextInput from "./base";
import "./styles.scss";

export interface PasswordInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  className?: string;
  autoFocus?: boolean;
  hideValidation?: boolean;
  serverValidity?: string | null;
  applyPasswordRules?: boolean;
  mustMatch?: string;
  labelVariant?: LabelVariant;
}

export default function PasswordInput({
  autoFocus,
  label,
  onChange,
  value,
  className,
  required,
  hideValidation,
  serverValidity,
  applyPasswordRules,
  mustMatch,
  labelVariant,
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const parse = useCallback(
    (value: string): { invalid: false; value: string } | { invalid: string } => {
      if (mustMatch) {
        return value === mustMatch ? { invalid: false, value } : { invalid: "Your passwords do not match" };
      }
      if (applyPasswordRules) {
        const invalid = awsPasswordValidation(value);
        return invalid ? { invalid } : { invalid: false, value };
      }
      return { invalid: false, value };
    },
    [applyPasswordRules, mustMatch],
  );

  return (
    <TextInput<string>
      label={label}
      value={value}
      onChange={onChange}
      type={{
        inputType: showPassword ? "text" : "password",
        toString: stringToString,
        parse,
      }}
      button={{
        label: showPassword ? "Hide password" : "Show password",
        icon: showPassword ? "eyeStrikethrough" : "eye",
        onClick: () => setShowPassword((show) => !show),
        isActive: showPassword,
      }}
      serverValidity={serverValidity}
      autoFocus={autoFocus}
      className={className}
      required={required}
      hideValidation={hideValidation}
      labelVariant={labelVariant}
    />
  );
}

function awsPasswordValidation(password: string) {
  const length = password.length < 10;
  const whiteSpace = /(\s)/.test(password);
  const number = !/(?=[0-9])/.test(password);
  const lowercase = !/(?=[a-z])/.test(password);
  const uppercase = !/(?=[A-Z])/.test(password);
  const special = !/(?=\W)/.test(password);
  if (!password) return "";
  if (length || whiteSpace || number || lowercase || uppercase || special) {
    return "Password does not meet requirements";
  }

  return "";
}

function stringToString(value: string) {
  return value;
}

export function AwsPasswordValidationAlert() {
  return (
    <Alert variant="info" className="ds-mb-5">
      Password must be at least 10 characters and include one uppercase letter, one lowercase letter, one number, and
      one special character. No spaces allowed.
    </Alert>
  );
}
